import React, { useState, useEffect } from 'react';
import api from '../../services/api';
import { toast } from 'react-toastify';
import { downloadInvoicePDF } from '../../utils/invoicePdf';

const fmt = (v) => '₦' + Number(v).toLocaleString('en-NG', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

export default function MyInvoices() {
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');

  const fetchInvoices = () => {
    api.get('/invoices/my/')
      .then((res) => setInvoices(res.data.results || res.data || []))
      .catch(() => toast.error('Failed to load invoices'))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    fetchInvoices();

    // Re-fetch when the user switches back to this tab so admin deletions
    // are reflected without a full page reload.
    const handleVisibility = () => {
      if (document.visibilityState === 'visible') fetchInvoices();
    };
    document.addEventListener('visibilitychange', handleVisibility);
    return () => document.removeEventListener('visibilitychange', handleVisibility);
  }, []);

  const filtered = invoices.filter((inv) => {
    const q = search.toLowerCase();
    return (
      inv.reference_code?.toLowerCase().includes(q) ||
      inv.client_name?.toLowerCase().includes(q) ||
      inv.survey_type?.toLowerCase().includes(q) ||
      inv.location?.toLowerCase().includes(q)
    );
  });

  return (
    <div className="max-w-7xl mx-auto">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">My Invoices</h1>
          <p className="text-sm text-gray-500 mt-1">Scale of Fees invoices generated for your company</p>
        </div>
        <input
          type="text"
          placeholder="Search invoices..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="px-4 py-2.5 rounded-lg border border-gray-300 text-sm focus:ring-2 focus:ring-[#0B5D1E]/30 focus:border-[#0B5D1E] outline-none w-full sm:w-72"
        />
      </div>

      {loading ? (
        <div className="flex justify-center py-20">
          <div className="w-10 h-10 border-4 border-[#0B5D1E]/20 border-t-[#0B5D1E] rounded-full animate-spin" />
        </div>
      ) : filtered.length === 0 ? (
        <div className="text-center py-20 text-gray-400">
          <svg className="w-16 h-16 mx-auto mb-4 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
          </svg>
          <p className="text-lg font-medium">No invoices yet</p>
          <p className="text-sm mt-1">Invoices will appear here when the admin generates one for your company.</p>
        </div>
      ) : (
        <div className="bg-white rounded-xl shadow-sm border overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-gray-50 border-b text-left">
                  <th className="px-4 py-3 font-semibold text-gray-600">Ref Code</th>
                  <th className="px-4 py-3 font-semibold text-gray-600">Client</th>
                  <th className="px-4 py-3 font-semibold text-gray-600">Survey Type</th>
                  <th className="px-4 py-3 font-semibold text-gray-600">Location</th>
                  <th className="px-4 py-3 font-semibold text-gray-600">Land Size</th>
                  <th className="px-4 py-3 font-semibold text-gray-600 text-right">Amount</th>
                  <th className="px-4 py-3 font-semibold text-gray-600 text-right">VAT</th>
                  <th className="px-4 py-3 font-semibold text-gray-600 text-right">Total</th>
                  <th className="px-4 py-3 font-semibold text-gray-600">Date</th>
                  <th className="px-4 py-3 font-semibold text-gray-600 text-center">PDF</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {filtered.map((inv) => (
                  <tr key={inv.id} className="hover:bg-gray-50 transition">
                    <td className="px-4 py-3 font-bold text-[#0B5D1E] whitespace-nowrap">{inv.reference_code}</td>
                    <td className="px-4 py-3 text-gray-800">{inv.client_name || '—'}</td>
                    <td className="px-4 py-3 text-gray-700">{inv.survey_type}</td>
                    <td className="px-4 py-3 text-gray-700 max-w-[200px] truncate">{inv.location}</td>
                    <td className="px-4 py-3 text-gray-700">{inv.land_size || '—'}</td>
                    <td className="px-4 py-3 text-right text-gray-800 whitespace-nowrap">{fmt(inv.amount)}</td>
                    <td className="px-4 py-3 text-right text-gray-600 whitespace-nowrap">{fmt(inv.vat)}</td>
                    <td className="px-4 py-3 text-right font-bold text-[#0B5D1E] whitespace-nowrap">{fmt(inv.total)}</td>
                    <td className="px-4 py-3 text-gray-500 whitespace-nowrap">
                      {new Date(inv.created_at).toLocaleDateString('en-NG', { year: 'numeric', month: 'short', day: 'numeric' })}
                    </td>
                    <td className="px-4 py-3 text-center">
                      <button
                        onClick={() => downloadInvoicePDF(inv, inv.company_logo)}
                        className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-lg bg-[#0B5D1E] text-white text-xs font-semibold hover:bg-[#094A18] transition"
                        title="Download PDF"
                      >
                        <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                        </svg>
                        PDF
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
